import React, { useState, useMemo, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calendar, 
  Filter, 
  RefreshCw, 
  Download, 
  Layers, 
  Sparkles, 
  Plus, 
  Trash2, 
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  FileSpreadsheet,
  CheckCircle2,
  HelpCircle,
  Play,
  Edit
} from 'lucide-react';
import { 
  ComposedChart,
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area,
  Legend
} from 'recharts';
import { supabase } from '../../lib/supabase';
import { financeiroService } from '../../services/financeiroService';
import { Conta, Categoria, TipoContaId } from '../../types/financeiro';
import { CategorizarModal } from './CategorizarModal';

const COLORS = ['#ef4444', '#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#64748b'];

interface SimulatedTransaction {
  id: string;
  descricao: string;
  tipo: 'entrada' | 'saida';
  valor: number;
  dt_vencimento: string;
}


// Função utilitária para garantir que as datas YYYY-MM-DD não sofram com timezone shifts
function parseLocalSafely(dateStr: string): Date {
  if (!dateStr) return new Date();
  const [year, month, day] = dateStr.split('T')[0].split('-');
  return new Date(Number(year), Number(month) - 1, Number(day));
}

function getYearMonth(dateStr: string): string {
  if (!dateStr) return '';
  const [year, month] = dateStr.split('T')[0].split('-');
  return `${year}-${month}`;
}

function formatDateBR(dateStr?: string): string {
  if (!dateStr) return '-';
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
}

export function FluxoCaixaDashboard() {
  const [contas, setContas] = useState<Conta[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date();
    // Default to Jan 1 of current year
    return `${d.getFullYear()}-01-01`;
  });
  const [dataFim, setDataFim] = useState(() => {
    const d = new Date();
    // Default to Dec 31 of current year
    return `${d.getFullYear()}-12-31`;
  });
  const [regime, setRegime] = useState<'caixa' | 'competencia'>('caixa'); // caixa = dt_pagamento, competencia = dt_vencimento
  const [statusFiltro, setStatusFiltro] = useState<'todos' | 'realizados' | 'previstos'>('todos');

  // Simulações (Cenários "What-If")
  const [simulacoes, setSimulacoes] = useState<SimulatedTransaction[]>([]);
  const [novaSimDesc, setNovaSimDesc] = useState('');
  const [novaSimValor, setNovaSimValor] = useState('');
  const [novaSimTipo, setNovaSimTipo] = useState<'entrada' | 'saida'>('saida');
  const [novaSimData, setNovaSimData] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return today;
  });
  const [showSimForm, setShowSimForm] = useState(false);

  // Categorização de Lançamentos Pendentes
  const [isCategorizarOpen, setIsCategorizarOpen] = useState(false);

  // Carregar dados
  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setLoading(true);
      // Fetching up to 500 records to have a robust dataset for the dashboard
      const listContas = await financeiroService.getContas();
      const listCategorias = await financeiroService.getCategorias();
      
      setContas(listContas);
      setCategorias(listCategorias);
    } catch (err) {
      console.error('Error fetching data for Cash Flow:', err);
    } finally {
      setLoading(false);
    }
  }

  // Mapear categoria_id -> nome
  const catMap = useMemo(() => {
    const map: Record<number, string> = {};
    categorias.forEach(c => {
      map[c.id] = c.nome;
    });
    return map;
  }, [categorias]);

  // Transações consolidadas (originais + simuladas)
  const allTransactions = useMemo(() => {
    const mappedOriginals = contas.map(c => {
      const isPaid = !!c.dt_pagamento;
      const finalValue = isPaid ? (Number(c.valor_pago) || Number(c.valor) || 0) : (Number(c.valor) || 0);
      
      return {
        id: `real-${c.id}`,
        descricao: c.descricao || 'Lançamento sem descrição',
        tipo: Number(c.tipo_conta_id) === TipoContaId.RECEBER ? 'entrada' : 'saida',
        valor: finalValue,
        dt_vencimento: c.dt_vencimento || '',
        dt_pagamento: c.dt_pagamento || '',
        categoria_id: c.categoria_id,
        isSimulated: false
      };
    });

    const mappedSimulations = simulacoes.map(s => ({
      id: s.id,
      descricao: `[SIMULADO] ${s.descricao}`,
      tipo: s.tipo,
      valor: s.valor,
      dt_vencimento: s.dt_vencimento,
      dt_pagamento: s.dt_vencimento, // Assume paid on vencimento for cash basis simulations
      categoria_id: null as any,
      isSimulated: true
    }));

    return [...mappedOriginals, ...mappedSimulations];
  }, [contas, simulacoes]);

  // Filtrar dados pelo período e critérios
  const filteredTransactions = useMemo(() => {
    const inicioTime = parseLocalSafely(dataInicio).getTime();
    const fimTime = parseLocalSafely(dataFim).getTime();

    return allTransactions.filter(t => {
      // Determinar a data de referência baseado no regime
      const dateStr = regime === 'caixa' ? (t.dt_pagamento || t.dt_vencimento) : t.dt_vencimento;
      if (!dateStr) return false;

      const dateTime = parseLocalSafely(dateStr).getTime();
      const inPeriod = dateTime >= inicioTime && dateTime <= fimTime;

      // Filtragem por status de realização
      if (statusFiltro === 'realizados') {
        // Regime caixa exige dt_pagamento preenchido
        if (!t.isSimulated && !t.dt_pagamento) return false;
      } else if (statusFiltro === 'previstos') {
        if (!t.isSimulated && t.dt_pagamento) return false;
      }

      return inPeriod;
    });
  }, [allTransactions, dataInicio, dataFim, regime, statusFiltro]);

  // Transações sem categoria no período selecionado
  const uncategorizedTransactions = useMemo(() => {
    return filteredTransactions.filter(t => !t.isSimulated && !t.categoria_id);
  }, [filteredTransactions]);

  // Saldo de períodos anteriores (para saldo acumulado)
  const saldoAnterior = useMemo(() => {
    const inicioTime = parseLocalSafely(dataInicio).getTime();
    let saldo = 0;

    allTransactions.forEach(t => {
      if (t.isSimulated) return;
      const dateStr = regime === 'caixa' ? (t.dt_pagamento || t.dt_vencimento) : t.dt_vencimento;
      if (!dateStr) return;

      const dateTime = parseLocalSafely(dateStr).getTime();
      if (dateTime < inicioTime) {
        if (statusFiltro === 'realizados' && !t.dt_pagamento) return;
        if (statusFiltro === 'previstos' && t.dt_pagamento) return;

        if (t.tipo === 'entrada') {
          saldo += t.valor;
        } else {
          saldo -= t.valor;
        }
      }
    });

    return saldo;
  }, [allTransactions, dataInicio, regime, statusFiltro]);

  // Callback para salvar a categoria escolhida
  const handleCategorizeTransaction = async (id: number, categoriaId: number) => {
    await financeiroService.updateConta(id, { categoria_id: categoriaId });
    setContas(prev => prev.map(c => c.id === id ? { ...c, categoria_id: categoriaId } : c));
  };

  // Cálculo dos KPIs
  const kpis = useMemo(() => {
    let entradas = 0;
    let saidas = 0;
    let pendenteEntrada = 0;
    let pendenteSaida = 0;

    filteredTransactions.forEach(t => {
      const isPaid = t.isSimulated || !!t.dt_pagamento;
      if (t.tipo === 'entrada') {
        entradas += t.valor;
        if (!isPaid) pendenteEntrada += t.valor;
      } else {
        saidas += t.valor;
        if (!isPaid) pendenteSaida += t.valor;
      }
    });

    const saldo = entradas - saidas;
    const lucratividade = entradas > 0 ? (saldo / entradas) * 100 : 0;

    return {
      entradas,
      saidas,
      saldo,
      lucratividade,
      pendenteEntrada,
      pendenteSaida
    };
  }, [filteredTransactions]);

  // Gráfico mensal (Entradas, Saídas e Saldo Acumulado)
  const monthlyChartData = useMemo(() => {
    const monthlyGroups: Record<string, { yearMonth: string; name: string; entradas: number; saidas: number }> = {};
    
    // Inicializar os meses no intervalo de datas
    const start = parseLocalSafely(dataInicio);
    const end = parseLocalSafely(dataFim);
    let curr = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (curr <= end) {
      const yearMonth = `${curr.getFullYear()}-${String(curr.getMonth() + 1).padStart(2, '0')}`;
      const label = curr.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      monthlyGroups[yearMonth] = { yearMonth, name: label, entradas: 0, saidas: 0 };
      curr.setMonth(curr.getMonth() + 1);
    }

    filteredTransactions.forEach(t => {
      const dateStr = regime === 'caixa' ? (t.dt_pagamento || t.dt_vencimento) : t.dt_vencimento;
      if (!dateStr) return;
      const yearMonth = getYearMonth(dateStr);
      
      if (monthlyGroups[yearMonth]) {
        if (t.tipo === 'entrada') {
          monthlyGroups[yearMonth].entradas += t.valor;
        } else {
          monthlyGroups[yearMonth].saidas += t.valor;
        }
      }
    });

    // Converter para array e calcular saldo acumulado
    const sortedKeys = Object.keys(monthlyGroups).sort();
    let saldoAcumulado = saldoAnterior;
    
    return sortedKeys.map(key => {
      const g = monthlyGroups[key];
      const saldoMes = g.entradas - g.saidas;
      saldoAcumulado += saldoMes;
      return {
        ...g,
        saldo: saldoMes,
        acumulado: saldoAcumulado
      };
    });
  }, [filteredTransactions, dataInicio, dataFim, regime, saldoAnterior]);

  // Gráficos de Categorias (Entradas e Saídas)
  const categoriesCharts = useMemo(() => {
    const entradasMap: Record<string, number> = {};
    const saidasMap: Record<string, number> = {};

    filteredTransactions.forEach(t => {
      const catName = t.categoria_id ? (catMap[t.categoria_id] || 'Outros') : (t.isSimulated ? 'Simulação' : 'Sem Categoria');
      if (t.tipo === 'entrada') {
        entradasMap[catName] = (entradasMap[catName] || 0) + t.valor;
      } else {
        saidasMap[catName] = (saidasMap[catName] || 0) + t.valor;
      }
    });

    const entradasData = Object.entries(entradasMap).map(([name, value]) => ({ name, value }));
    const saidasData = Object.entries(saidasMap).map(([name, value]) => ({ name, value }));

    return {
      entradas: entradasData,
      saidas: saidasData
    };
  }, [filteredTransactions, catMap]);

  // Estrutura para a grade do Demonstrativo de Fluxo de Caixa (DFC)
  const dfcData = useMemo(() => {
    // Meses Label Map
    const monthsLabels = monthlyChartData.map(m => ({ key: m.yearMonth, label: m.name }));

    // Mapeamento de Categorias de Entrada
    const inflowCats: Record<string, Record<string, number>> = {};
    // Mapeamento de Categorias de Saída
    const outflowCats: Record<string, Record<string, number>> = {};

    filteredTransactions.forEach(t => {
      const dateStr = regime === 'caixa' ? (t.dt_pagamento || t.dt_vencimento) : t.dt_vencimento;
      if (!dateStr) return;
      
      const yearMonth = getYearMonth(dateStr);
      // Skip if it doesn't fit the month chart
      if (!monthsLabels.find(m => m.key === yearMonth)) return;
      const catName = t.categoria_id ? (catMap[t.categoria_id] || 'Outros') : (t.isSimulated ? 'Simulado' : 'Sem Categoria');

      if (t.tipo === 'entrada') {
        if (!inflowCats[catName]) inflowCats[catName] = {};
        inflowCats[catName][yearMonth] = (inflowCats[catName][yearMonth] || 0) + t.valor;
      } else {
        if (!outflowCats[catName]) outflowCats[catName] = {};
        outflowCats[catName][yearMonth] = (outflowCats[catName][yearMonth] || 0) + t.valor;
      }
    });

    return {
      monthsLabels,
      inflow: inflowCats,
      outflow: outflowCats
    };
  }, [filteredTransactions, catMap, monthlyChartData, regime]);

  // Adicionar transação simulada
  const handleAddSimulation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!novaSimDesc || !novaSimValor) return;

    const newSim: SimulatedTransaction = {
      id: `sim-${Date.now()}`,
      descricao: novaSimDesc,
      tipo: novaSimTipo,
      valor: parseFloat(novaSimValor) || 0,
      dt_vencimento: novaSimData
    };

    setSimulacoes(prev => [...prev, newSim]);
    setNovaSimDesc('');
    setNovaSimValor('');
    setShowSimForm(false);
  };

  // Remover transação simulada
  const handleRemoveSimulation = (id: string) => {
    setSimulacoes(prev => prev.filter(s => s.id !== id));
  };

  // Exportar dados em CSV
  const handleExportCSV = () => {
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += 'Descricao;Tipo;Valor;Vencimento;Pagamento;Categoria\n';

    filteredTransactions.forEach(t => {
      const catName = t.categoria_id ? (catMap[t.categoria_id] || '') : (t.isSimulated ? 'Simulado' : '');
      const row = [
        t.descricao.replace(/;/g, ','),
        t.tipo === 'entrada' ? 'Recebimento' : 'Pagamento',
        t.valor,
        t.dt_vencimento,
        t.dt_pagamento || '',
        catName
      ].join(';');
      csvContent += row + '\n';
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `fluxo_de_caixa_${dataInicio}_a_${dataFim}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Header do Fluxo de Caixa */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <span className="text-[10px] bg-red-50 text-red-600 font-black uppercase tracking-widest px-2.5 py-1 rounded-full border border-red-100">
            Módulo Financeiro Avançado
          </span>
          <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight mt-2 flex items-center gap-2">
            <Layers className="w-6 h-6 text-red-600" />
            Demonstrativo de Fluxo de Caixa (DFC)
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Análise retroativa de caixa, regimes financeiros e simulação de cenários futuros.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm"
          >
            <Download className="w-4 h-4 text-slate-500" />
            Exportar CSV
          </button>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Recarregar
          </button>
        </div>
      </div>

      {/* Barra de Filtros Inteligentes */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-red-500" /> Período Inicial
          </label>
          <input 
            type="date" 
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none focus:border-red-500 focus:bg-white transition-all"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-red-500" /> Período Final
          </label>
          <input 
            type="date" 
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none focus:border-red-500 focus:bg-white transition-all"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-red-500" /> Regime Financeiro
          </label>
          <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button
              onClick={() => setRegime('caixa')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${
                regime === 'caixa' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              title="Baseado na data de pagamento efetivo"
            >
              Regime de Caixa
            </button>
            <button
              onClick={() => setRegime('competencia')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${
                regime === 'competencia' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              title="Baseado na data de vencimento (planejado)"
            >
              Competência
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-red-500" /> Situação dos Títulos
          </label>
          <select
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value as any)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none focus:border-red-500 focus:bg-white transition-all"
          >
            <option value="todos">Todos (Previsto + Realizado)</option>
            <option value="realizados">Realizado (Apenas Quitados)</option>
            <option value="previstos">Previsto (Apenas em Aberto)</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Entradas */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl group-hover:scale-110 transition-transform">
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="flex items-center text-xs font-bold text-emerald-600">
              <ArrowUpRight className="w-4 h-4 mr-0.5" /> Entradas
            </span>
          </div>
          <div>
            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total de Ingressos</h4>
            <div className="text-2xl font-black text-slate-900 mt-1">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(kpis.entradas)}
            </div>
            {kpis.pendenteEntrada > 0 && (
              <span className="text-[10px] text-slate-500 font-medium block mt-1.5">
                Pendente: <span className="font-bold">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(kpis.pendenteEntrada)}</span>
              </span>
            )}
          </div>
        </div>

        {/* Saídas */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-red-50 text-red-600 rounded-xl group-hover:scale-110 transition-transform">
              <TrendingDown className="w-6 h-6" />
            </div>
            <span className="flex items-center text-xs font-bold text-red-600">
              <ArrowDownRight className="w-4 h-4 mr-0.5" /> Saídas
            </span>
          </div>
          <div>
            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total de Desembolsos</h4>
            <div className="text-2xl font-black text-slate-900 mt-1">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(kpis.saidas)}
            </div>
            {kpis.pendenteSaida > 0 && (
              <span className="text-[10px] text-slate-500 font-medium block mt-1.5">
                A pagar: <span className="font-bold">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(kpis.pendenteSaida)}</span>
              </span>
            )}
          </div>
        </div>

        {/* Saldo Líquido */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group">
          <div className="flex items-center justify-between mb-3">
            <div className={`p-3 rounded-xl group-hover:scale-110 transition-transform ${
              kpis.saldo >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
            }`}>
              <DollarSign className="w-6 h-6" />
            </div>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              kpis.saldo >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
            }`}>
              {kpis.saldo >= 0 ? 'Superávit' : 'Déficit'}
            </span>
          </div>
          <div>
            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Resultado Operacional</h4>
            <div className={`text-2xl font-black mt-1 ${kpis.saldo >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(kpis.saldo)}
            </div>
            <span className="text-[10px] text-slate-500 font-medium block mt-1.5">
              Resultado final do período
            </span>
          </div>
        </div>

        {/* Rentabilidade */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl group-hover:scale-110 transition-transform">
              <Sparkles className="w-6 h-6" />
            </div>
            <span className="flex items-center text-xs font-bold text-indigo-600">
              Rentabilidade
            </span>
          </div>
          <div>
            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Margem de Caixa</h4>
            <div className="text-2xl font-black text-slate-900 mt-1">
              {kpis.lucratividade.toFixed(1)}%
            </div>
            <span className="text-[10px] text-slate-500 font-medium block mt-1.5">
              Aproveitamento de receita
            </span>
          </div>
        </div>
      </div>

      {/* Gráficos Principais */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gráfico 1: Evolução mensal e Saldo Acumulado */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-red-600" />
              Evolução e Tendência do Fluxo de Caixa
            </h3>
            <div className="flex flex-wrap items-center gap-4 text-xs font-semibold">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 bg-emerald-500 rounded-sm"></span> Entradas
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 bg-red-400 rounded-sm"></span> Saídas
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-1 bg-indigo-600 inline-block rounded-sm"></span> Acumulado
              </span>
            </div>
          </div>

          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthlyChartData} margin={{ top: 10, right: -5, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#94a3b8', fontSize: 11 }} 
                  dy={10}
                />
                <YAxis 
                  yAxisId="left"
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#94a3b8', fontSize: 11 }} 
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#6366f1', fontSize: 11 }} 
                />
                <Tooltip 
                  cursor={{ fill: '#f8fafc' }}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  formatter={(value: any) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value))}
                />
                <Bar yAxisId="left" dataKey="entradas" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
                <Bar yAxisId="left" dataKey="saidas" fill="#f87171" radius={[4, 4, 0, 0]} barSize={20} />
                <Line yAxisId="right" type="monotone" dataKey="acumulado" stroke="#4f46e5" strokeWidth={3.5} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico 2: Composição de Despesas */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
              <TrendingDown className="w-5 h-5 text-red-600" />
              Categorias de Desembolso
            </h3>
            
            {categoriesCharts.saidas.length === 0 ? (
              <div className="h-[220px] flex items-center justify-center text-sm text-slate-400 italic">
                Nenhuma despesa no período.
              </div>
            ) : (
              <div className="h-[200px] w-full relative flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoriesCharts.saidas}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={75}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {categoriesCharts.saidas.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: any) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value))}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute flex flex-col items-center justify-center">
                  <span className="text-[10px] text-slate-400 font-bold uppercase">Total Saídas</span>
                  <span className="text-base font-black text-slate-900">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(kpis.saidas)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2 mt-4 max-h-[140px] overflow-y-auto pr-1">
            {categoriesCharts.saidas.map((item, index) => {
              const perc = kpis.saidas > 0 ? (item.value / kpis.saidas) * 100 : 0;
              return (
                <div key={item.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                    <span className="text-slate-600 truncate">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-2 font-bold text-slate-800">
                    <span>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.value)}</span>
                    <span className="text-[10px] text-slate-400">({perc.toFixed(0)}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Simulador de Cenários "What-if" */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-red-50 text-red-600 rounded-lg">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">Cenários de Simulação ("What-If")</h3>
              <p className="text-xs text-slate-500">Adicione receitas ou despesas hipotéticas para analisar o impacto imediato no saldo acumulado.</p>
            </div>
          </div>

          <button
            onClick={() => setShowSimForm(!showSimForm)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Simular Lançamento
          </button>
        </div>

        {/* Formulário de Simulação */}
        {showSimForm && (
          <form onSubmit={handleAddSimulation} className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="md:col-span-2">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Descrição Simulada</label>
              <input 
                type="text" 
                placeholder="Ex: Aquisição de Caminhão Cargo, Contrato Campinas" 
                value={novaSimDesc}
                onChange={(e) => setNovaSimDesc(e.target.value)}
                required
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-800 outline-none focus:border-red-500 transition-all"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Tipo de Lançamento</label>
              <select
                value={novaSimTipo}
                onChange={(e) => setNovaSimTipo(e.target.value as any)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-800 outline-none focus:border-red-500 transition-all"
              >
                <option value="saida">Saída (Custo/Despesa)</option>
                <option value="entrada">Entrada (Recebimento)</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Valor do Lançamento (R$)</label>
              <input 
                type="number" 
                step="0.01"
                placeholder="0.00"
                value={novaSimValor}
                onChange={(e) => setNovaSimValor(e.target.value)}
                required
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-800 outline-none focus:border-red-500 transition-all"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Data Estimada</label>
              <input 
                type="date" 
                value={novaSimData}
                onChange={(e) => setNovaSimData(e.target.value)}
                required
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-800 outline-none focus:border-red-500 transition-all"
              />
            </div>
            <div className="md:col-span-5 flex justify-end gap-2 mt-1">
              <button
                type="button"
                onClick={() => setShowSimForm(false)}
                className="px-4 py-1.5 text-xs font-bold text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Injetar no DFC
              </button>
            </div>
          </form>
        )}

        {/* Lista de Simulações Injetadas */}
        {simulacoes.length > 0 ? (
          <div className="border border-slate-200 rounded-xl overflow-hidden mt-3 bg-slate-50 p-3">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Simulações Ativas</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {simulacoes.map(s => (
                <div key={s.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                  <div>
                    <h5 className="text-xs font-bold text-slate-800">{s.descricao}</h5>
                    <p className="text-[10px] text-slate-400 font-medium">Data: {formatDateBR(s.dt_vencimento)} • {s.tipo === 'entrada' ? 'Receita' : 'Despesa'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-bold ${s.tipo === 'entrada' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {s.tipo === 'entrada' ? '+' : '-'} {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(s.valor)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSimulation(s.id)}
                      className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-4 bg-slate-50 rounded-xl text-xs text-slate-500 font-medium italic border border-dashed border-slate-200">
            Nenhum lançamento simulado ativo no demonstrativo.
          </div>
        )}
      </div>

      {/* Grade Demonstrativa do Fluxo de Caixa (DFC) */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
          <FileSpreadsheet className="w-5 h-5 text-red-600" />
          Demonstrativo Mensal Estruturado (DFC)
        </h3>

        <div className="overflow-x-auto border border-slate-200 rounded-xl">
          <table className="min-w-full divide-y divide-slate-100 text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-black text-slate-500 uppercase tracking-wider min-w-[200px]">Linhas de Fluxo</th>
                {dfcData.monthsLabels.map(m => (
                  <th key={m.key} className="px-4 py-3 text-right font-black text-slate-500 uppercase tracking-wider min-w-[100px]">{m.label}</th>
                ))}
                <th className="px-4 py-3 text-right font-black text-slate-900 uppercase tracking-wider bg-slate-100 min-w-[120px]">Total Período</th>
              </tr>
            </thead>
            
            <tbody className="bg-white divide-y divide-slate-100 font-medium">
              {/* SEÇÃO SALDO INICIAL */}
              <tr className="bg-slate-200/60 text-slate-900 font-black border-b-2 border-slate-300">
                <td className="px-4 py-3 uppercase">SALDO INICIAL DO PERÍODO</td>
                {monthlyChartData.map((m, index) => {
                  const inicial = index === 0 ? saldoAnterior : monthlyChartData[index - 1].acumulado;
                  return (
                    <td key={`init-${m.yearMonth}`} className={`px-4 py-3 text-right ${inicial >= 0 ? 'text-indigo-900' : 'text-red-800'}`}>
                      {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(inicial)}
                    </td>
                  );
                })}
                <td className={`px-4 py-3 text-right bg-slate-300/80 font-black ${saldoAnterior >= 0 ? 'text-indigo-950' : 'text-red-950'}`}>
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(saldoAnterior)}
                </td>
              </tr>

              {/* SEÇÃO 1: RECEITAS / ENTRADAS */}
              <tr className="bg-emerald-50/40 text-emerald-900 font-bold">
                <td className="px-4 py-2.5">(=) INGRESSOS / ENTRADAS</td>
                {monthlyChartData.map(m => (
                  <td key={m.yearMonth} className="px-4 py-2.5 text-right">
                    {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(m.entradas)}
                  </td>
                ))}
                <td className="px-4 py-2.5 text-right bg-emerald-100/60 font-black">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(kpis.entradas)}
                </td>
              </tr>

              {/* Categorias de Entrada */}
              {Object.keys(dfcData.inflow).map(catName => {
                let rowTotal = 0;
                return (
                  <tr key={`in-${catName}`} className="hover:bg-slate-50 text-slate-600">
                    <td className="px-4 py-2 pl-8 text-slate-500 italic">
                      {catName === 'Sem Categoria' ? (
                        <button
                          onClick={() => setIsCategorizarOpen(true)}
                          className="text-red-600 hover:text-red-700 font-bold hover:underline inline-flex items-center gap-1.5 transition-colors cursor-pointer text-left focus:outline-none"
                          title="Clique para categorizar esses lançamentos"
                        >
                          <span>Sem Categoria</span>
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        catName
                      )}
                    </td>
                    {dfcData.monthsLabels.map(m => {
                      const val = dfcData.inflow[catName]?.[m.key] || 0;
                      rowTotal += val;
                      return (
                        <td key={m.key} className="px-4 py-2 text-right">
                          {val > 0 ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val) : '-'}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2 text-right bg-slate-50 text-slate-800 font-bold">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(rowTotal)}
                    </td>
                  </tr>
                );
              })}

              {/* SEÇÃO 2: SAÍDAS / DESPESAS */}
              <tr className="bg-red-50/40 text-red-900 font-bold">
                <td className="px-4 py-2.5">(-) DESEMBOLSOS / SAÍDAS</td>
                {monthlyChartData.map(m => (
                  <td key={m.yearMonth} className="px-4 py-2.5 text-right">
                    {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(m.saidas)}
                  </td>
                ))}
                <td className="px-4 py-2.5 text-right bg-red-100/60 font-black">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(kpis.saidas)}
                </td>
              </tr>

              {/* Categorias de Saída */}
              {Object.keys(dfcData.outflow).map(catName => {
                let rowTotal = 0;
                return (
                  <tr key={`out-${catName}`} className="hover:bg-slate-50 text-slate-600">
                    <td className="px-4 py-2 pl-8 text-slate-500 italic">
                      {catName === 'Sem Categoria' ? (
                        <button
                          onClick={() => setIsCategorizarOpen(true)}
                          className="text-red-600 hover:text-red-700 font-bold hover:underline inline-flex items-center gap-1.5 transition-colors cursor-pointer text-left focus:outline-none"
                          title="Clique para categorizar esses lançamentos"
                        >
                          <span>Sem Categoria</span>
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        catName
                      )}
                    </td>
                    {dfcData.monthsLabels.map(m => {
                      const val = dfcData.outflow[catName]?.[m.key] || 0;
                      rowTotal += val;
                      return (
                        <td key={m.key} className="px-4 py-2 text-right">
                          {val > 0 ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val) : '-'}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2 text-right bg-slate-50 text-slate-800 font-bold">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(rowTotal)}
                    </td>
                  </tr>
                );
              })}

              {/* SEÇÃO 3: RESULTADOS OPERACIONAIS */}
              <tr className="bg-slate-100 text-slate-900 font-bold border-t-2 border-slate-300">
                <td className="px-4 py-3">(=) SALDO OPERACIONAL</td>
                {monthlyChartData.map(m => (
                  <td key={m.name} className={`px-4 py-3 text-right font-black ${m.saldo >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(m.saldo)}
                  </td>
                ))}
                <td className={`px-4 py-3 text-right font-black bg-slate-200 ${kpis.saldo >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(kpis.saldo)}
                </td>
              </tr>

              <tr className="bg-slate-200/60 text-slate-900 font-black">
                <td className="px-4 py-3 uppercase">(=) SALDO ACUMULADO (CAIXA FINAL)</td>
                {monthlyChartData.map(m => (
                  <td key={m.name} className={`px-4 py-3 text-right ${m.acumulado >= 0 ? 'text-indigo-900' : 'text-red-800'}`}>
                    {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(m.acumulado)}
                  </td>
                ))}
                <td className="px-4 py-3 text-right bg-slate-300/80 font-black text-indigo-950">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(saldoAnterior + kpis.saldo)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <CategorizarModal
        isOpen={isCategorizarOpen}
        onClose={() => setIsCategorizarOpen(false)}
        transactions={uncategorizedTransactions}
        categories={categorias}
        onCategorize={handleCategorizeTransaction}
      />
    </div>
  );
}
